import { useRef, useState } from "react";
import { useMutation } from "@apollo/client";
import { FiEdit, FiFile, FiImage, FiUpload } from "react-icons/fi";
import { parseError } from "apollo";
import Button from "components/base/Button";
import { Select } from "components/base/Form";
import { Alert } from "components/base/Toast";
import useUploadProtection from "hooks/useUploadProtection";
import {
  CREATE_MARKETING_MATERIAL,
  UPDATE_MARKETING_MATERIAL,
} from "./graphql";

function fileNameWithoutExtension(fileName = "") {
  const lastDotIndex = fileName.lastIndexOf(".");
  if (lastDotIndex <= 0) return fileName;
  return fileName.slice(0, lastDotIndex);
}

const MarketingMaterialFormModal = ({ material = null, refetch, hide }) => {
  const isEdit = !!material;
  const assetInputRef = useRef(null);
  const [createMarketingMaterial] = useMutation(CREATE_MARKETING_MATERIAL);
  const [updateMarketingMaterial] = useMutation(UPDATE_MARKETING_MATERIAL);
  const [submitting, setSubmitting] = useState(false);
  const [hasEditedName, setHasEditedName] = useState(isEdit);
  const [coverFile, setCoverFile] = useState(null);
  const [assetFile, setAssetFile] = useState(null);
  const [coverPreview, setCoverPreview] = useState(material?.coverSrc || null);
  const [name, setName] = useState(material?.name || "");
  const [category, setCategory] = useState(material?.category || "logo");

  useUploadProtection(
    submitting,
    "Upload is in progress. Leaving now will cancel the upload. Continue?",
  );

  const handleCoverChange = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.type?.startsWith("image/")) {
      Alert("error", "Cover must be an image file.");
      event.target.value = "";
      return;
    }

    setCoverFile(file);

    const reader = new FileReader();
    reader.onloadend = () => setCoverPreview(reader.result);
    reader.readAsDataURL(file);

    event.target.value = "";
  };

  const handleAssetChange = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setAssetFile(file);
    if (!hasEditedName) {
      setName(fileNameWithoutExtension(file.name));
    }
    event.target.value = "";
  };

  const handleSubmit = async () => {
    const trimmedName = name.trim();

    if (!isEdit && (!coverFile || !assetFile)) {
      Alert("error", "Please upload both cover image and material file.");
      return;
    }

    try {
      setSubmitting(true);

      if (isEdit) {
        const variables = { id: material.id };

        if (trimmedName && trimmedName !== material.name) {
          variables.name = trimmedName;
        }
        if (category !== material.category) {
          variables.category = category;
        }
        if (coverFile) {
          variables.cover = coverFile;
        }
        if (assetFile) {
          variables.asset = assetFile;
        }

        if (Object.keys(variables).length === 1) {
          Alert("error", "No changes to update.");
          setSubmitting(false);
          return;
        }

        await updateMarketingMaterial({ variables });
        Alert("success", "Marketing material updated successfully.");
      } else {
        await createMarketingMaterial({
          variables: {
            name: trimmedName || null,
            category,
            cover: coverFile,
            asset: assetFile,
          },
        });
        Alert("success", "Marketing material created successfully.");
      }

      await refetch();
      hide();
    } catch (error) {
      Alert("error", parseError(error));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center space-x-3">
        <FiEdit className="text-blue-500 text-2xl" />
        <h2 className="text-2xl font-bold">
          {isEdit ? "Edit Marketing Material" : "Create New Marketing Material"}
        </h2>
      </div>

      <div className="relative overflow-hidden rounded-xl shadow-lg group">
        {coverPreview ? (
          <>
            <img
              src={coverPreview}
              alt="Cover preview"
              className="w-full h-[28vh] min-h-[180px] max-h-[280px] object-cover"
            />
            <label className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity duration-300 cursor-pointer flex items-center justify-center">
              <div className="text-center text-white">
                <FiUpload className="mx-auto text-5xl mb-3" />
                <p className="text-lg font-semibold">Click to change cover</p>
                <p className="text-sm mt-1">PNG, JPG up to 10MB</p>
              </div>
              <input
                type="file"
                accept="image/*"
                onChange={handleCoverChange}
                className="hidden"
              />
            </label>
          </>
        ) : (
          <label className="block border-2 border-dashed border-gray-300 rounded-xl h-[28vh] min-h-[180px] max-h-[280px] cursor-pointer hover:border-blue-500 hover:bg-blue-50 transition-all">
            <div className="flex flex-col items-center justify-center h-full text-gray-500">
              <FiImage className="text-6xl mb-4" />
              <p className="text-lg font-semibold text-gray-700">
                Upload Cover Image <span className="text-red-500">*</span>
              </p>
              <p className="text-sm mt-2">PNG, JPG up to 10MB</p>
            </div>
            <input
              type="file"
              accept="image/*"
              onChange={handleCoverChange}
              className="hidden"
            />
          </label>
        )}
      </div>

      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium mb-2">Material Name</label>
          <input
            type="text"
            value={name}
            onChange={(event) => {
              setHasEditedName(true);
              setName(event.target.value);
            }}
            className="w-full px-4 py-3 rounded-lg border border-gray-300 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="Optional, defaults to material filename"
          />
        </div>

        <div>
          <label className="block text-sm font-medium mb-2">Category</label>
          <Select
            value={category}
            onChange={(event) => setCategory(event.target.value)}
            className="w-full"
            height={52}
          >
            <option value="logo">Logo</option>
          </Select>
        </div>

        <div>
          <label className="block text-sm font-medium mb-2">
            Material File {!isEdit ? <span className="text-red-500">*</span> : null}
          </label>
          <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
            <FiFile className="text-gray-400 text-xl flex-shrink-0" />
            <div className="flex-1 min-w-0">
              {assetFile ? (
                <span className="text-sm text-gray-700 truncate block">
                  {assetFile.name}
                </span>
              ) : material?.asset ? (
                <a
                  href={material.assetSrc}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-blue-600 hover:text-blue-700 truncate block"
                  title={material.asset}
                >
                  {material.asset.split("/").pop()}
                </a>
              ) : (
                <span className="text-sm text-gray-400">No file selected</span>
              )}
            </div>

            <input
              ref={assetInputRef}
              type="file"
              onChange={handleAssetChange}
              className="hidden"
            />
            <Button
              onClick={() => assetInputRef.current?.click()}
              className="flex items-center"
              icon={<FiUpload />}
            >
              {assetFile ? "Change" : isEdit ? "Replace" : "Upload"}
            </Button>
          </div>
        </div>
      </div>

      <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
        <Button onClick={hide}>Cancel</Button>
        <Button
          onClick={handleSubmit}
          primary
          loading={submitting}
          disabled={submitting}
        >
          {isEdit ? "Update Material" : "Create Material"}
        </Button>
      </div>
    </div>
  );
};

export default MarketingMaterialFormModal;
